import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import openPdf from '../../api/openPdf';

const statusLabels = {
  open: { label: 'Abierto', color: 'badge-info' },
  in_progress: { label: 'En proceso', color: 'badge-warning' },
  completed: { label: 'Completado', color: 'badge-success' },
  cancelled: { label: 'Cancelado', color: 'badge-danger' },
};

const CaseFileList = () => {
  const [caseFiles, setCaseFiles] = useState([]);
  const [pagination, setPagination] = useState({});
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [staleDays, setStaleDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '', procedure_type_id: '', assigned_user_id: '', search: '', stale: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (filters.status) params.status = filters.status;
      if (filters.procedure_type_id) params.procedure_type_id = filters.procedure_type_id;
      if (filters.assigned_user_id) params.assigned_user_id = filters.assigned_user_id;
      if (filters.search) params.search = filters.search;
      if (filters.stale) params.stale = filters.stale;

      const [cfRes, ptRes, usersRes, settingsRes] = await Promise.all([
        api.get('/case-files', { params }),
        procedureTypes.length ? Promise.resolve({ data: procedureTypes }) : api.get('/procedure-types'),
        users.length ? Promise.resolve({ data: users }) : api.get('/users'),
        api.get('/settings/system'),
      ]);

      setCaseFiles(cfRes.data.data);
      setPagination(cfRes.data);
      if (!procedureTypes.length) setProcedureTypes(ptRes.data);
      if (!users.length) setUsers(usersRes.data);
      const staleSetting = settingsRes.data.find(s => s.key === 'stale_case_file_days');
      if (staleSetting) setStaleDays(parseInt(staleSetting.value));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchData(1);
  }, [filters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este expediente?')) return;
    try {
      await api.delete(`/case-files/${id}`);
      fetchData(currentPage);
    } catch (error) {
      alert('Error al eliminar');
    }
  };

const handleExportPdf = () => {
  const params = {};
  if (filters.status)            params.status = filters.status;
  if (filters.procedure_type_id) params.procedure_type_id = filters.procedure_type_id;
  if (filters.assigned_user_id)  params.assigned_user_id = filters.assigned_user_id;
  if (filters.search)            params.search = filters.search;
  if (filters.stale)             params.stale = filters.stale;
  openPdf('/api/case-files/pdf/list', params);
};

  return (
    <div className="container-fluid">
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          Expedientes
          {pagination.total > 0 && (
            <span className="badge badge-secondary ml-2">{pagination.total}</span>
          )}
        </h4>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <h4 className="mb-0">
            Expedientes
            {pagination.total > 0 && (
              <span className="badge badge-secondary ml-2">{pagination.total}</span>
            )}
          </h4>
          <button className="btn btn-outline-danger btn-sm" onClick={handleExportPdf}>
            <i className="ti-printer mr-1"></i> Exportar PDF
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row filter-row align-items-center">
            <div className="col-12 col-md-3 mb-2 mb-md-0">
              <input type="text" className="form-control form-control-sm"
                placeholder="Buscar por título o cliente..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)} />
            </div>
            <div className="col-6 col-md-2 mb-2 mb-md-0">
              <select className="form-control form-control-sm" value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}>
                <option value="">Todos los estados</option>
                <option value="open">Abierto</option>
                <option value="in_progress">En proceso</option>
                <option value="completed">Completado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
            <div className="col-6 col-md-2 mb-2 mb-md-0">
              <select className="form-control form-control-sm" value={filters.procedure_type_id}
                onChange={(e) => handleFilterChange('procedure_type_id', e.target.value)}>
                <option value="">Todos los trámites</option>
                {procedureTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2 mb-2 mb-md-0">
              <select className="form-control form-control-sm" value={filters.assigned_user_id}
                onChange={(e) => handleFilterChange('assigned_user_id', e.target.value)}>
                <option value="">Todos los responsables</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <div className="custom-control custom-checkbox">
                <input type="checkbox" className="custom-control-input" id="staleFilter"
                  checked={filters.stale === '1'}
                  onChange={(e) => handleFilterChange('stale', e.target.checked ? '1' : '')} />
                <label className="custom-control-label" htmlFor="staleFilter">
                  <span className="text-danger">⚠</span> Sin actividad (+{staleDays}d)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla / Cards */}
      <div className="card">
        <div className="card-body p-0 mobile-card-list">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : caseFiles.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="ti-folder" style={{ fontSize: '3rem' }}></i>
              <p className="mt-2">No hay expedientes registrados</p>
            </div>
          ) : (
            <>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Cliente</th>
                    <th>Título</th>
                    <th className="mobile-hide">Trámite</th>
                    <th className="mobile-hide">Responsable</th>
                    <th className="mobile-hide">Docs</th>
                    <th>Tiempo</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {caseFiles.map((cf) => (
                    <tr key={cf.id} style={{ backgroundColor: cf.is_stale ? '#FFF8F0' : 'inherit' }}>
                      {/* Alerta stale */}
                      <td style={{ width: 32, padding: '8px 4px 8px 12px' }} className="td-alert">
                        {cf.is_stale && (
                          <span title={`Sin actividad por más de ${staleDays} días`}
                            style={{ fontSize: '16px' }}>⚠️</span>
                        )}
                      </td>

                      {/* Cliente — siempre visible */}
                      <td className="td-name">
                        <Link to={`/clients/${cf.client_id}`} className="text-primary font-weight-bold">
                          {cf.client?.name}
                        </Link>
                        {/* En móvil mostramos título debajo del cliente */}
                        <div className="d-md-none" style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                          {cf.title}
                        </div>
                      </td>

                      {/* Título — oculto en móvil (se muestra dentro de Cliente) */}
                      <td className="mobile-hide">{cf.title}</td>

                      {/* Trámite — oculto en móvil */}
                      <td className="mobile-hide" data-label="Trámite">
                        {cf.procedure_type?.name || '-'}
                      </td>

                      {/* Responsable — oculto en móvil */}
                      <td className="mobile-hide" data-label="Responsable">
                        {cf.assigned_user?.name || '-'}
                      </td>

                      {/* Documentos — oculto en móvil */}
                      <td className="mobile-hide">
                        <span className={`badge ${cf.document_stats?.delivered === cf.document_stats?.total && cf.document_stats?.total > 0 ? 'badge-success' : 'badge-warning'}`}>
                          {cf.document_stats?.delivered}/{cf.document_stats?.total}
                        </span>
                      </td>

                      {/* Tiempo — siempre visible */}
                      <td data-label="Tiempo">
                        <span className={`badge ${cf.is_stale ? 'badge-danger' : cf.status === 'completed' ? 'badge-light' : 'badge-secondary'}`}>
                          {cf.elapsed_time}
                        </span>
                      </td>

                      {/* Estado — siempre visible */}
                      <td data-label="Estado">
                        <span className={`badge ${statusLabels[cf.status]?.color}`}>
                          {statusLabels[cf.status]?.label}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td className="td-actions">
                        <div className="d-flex" style={{ gap: '6px' }}>
                          <Link to={`/case-files/${cf.id}`} className="btn btn-sm btn-outline-primary">
                            <i className="ti-eye"></i>
                          </Link>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(cf.id)}>
                            <i className="ti-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {pagination.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top flex-wrap" style={{ gap: '8px' }}>
                  <small className="text-muted">
                    Mostrando {pagination.from}–{pagination.to} de {pagination.total} expedientes
                  </small>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>‹</button>
                    </li>
                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(page)}>{page}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>›</button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseFileList;